"use client";

import { useEffect, useRef, useState } from "react";
import { m, AnimatePresence, useReducedMotion } from "framer-motion";
import { Download } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import { useAudioPlayerContext } from "@/contexts/audio-player-context";
import { useCookieConsent } from "@/components/ui/cookie-consent";

export interface StickyInstallCtaProps {
  /** True while the download is in flight — shows the spinner + "Downloading…". */
  isDownloading: boolean;
  /** Fires when the visitor taps "Download LockChime.wav". */
  onDownload: () => void;
  /** Force-disable the button (in addition to the busy state). */
  disabled?: boolean;
  /**
   * Once the file is on the device the strip stops re-offering it and points
   * at the receipt's install steps instead (`onShowInstallSteps`).
   */
  downloadCompleted?: boolean;
  /** Brings the visitor to the receipt's "You're set." heading. */
  onShowInstallSteps?: () => void;
}

/**
 * §5.3 — sticky mobile install CTA, extracted verbatim from
 * `app/sounds/[id]/sound-player-client.tsx`. Mobile-only (`sm:hidden`),
 * appears after 40% scroll so the sacred install path is never more than a
 * thumb away. Hides while a text field is focused so it never covers the
 * keyboard. Label unchanged from the primary CTA per §5.3 / §9.
 *
 * The caller decides whether to render it at all (e.g. the `downloadOnly`
 * guard stays in `sound-player-client.tsx`).
 *
 * NOTE: uses framer-motion `m.` components, so it MUST be rendered beneath a
 * `<LazyMotion features={domAnimation}>` ancestor (the sound player page
 * already provides one).
 */
/** The docked strip's measured height; read by app/globals.css. */
const INSTALL_CTA_HEIGHT_VAR = "--tls-install-cta-h";

export function StickyInstallCta({
  isDownloading,
  onDownload,
  disabled,
  downloadCompleted = false,
  onShowInstallSteps,
}: StickyInstallCtaProps) {
  // §5.3 — sticky mobile install CTA visibility (revealed after 40% scroll).
  const [showStickyCta, setShowStickyCta] = useState(false);
  const rm = useReducedMotion();

  // Bottom-surface coordination (2026-07 UX audit): the global mobile
  // mini-player also anchors to bottom-0 and carries its own download button.
  // When a preview is active the mini-player wins; this CTA yields so the two
  // strips never stack or obscure each other.
  const { currentSound } = useAudioPlayerContext();
  const miniPlayerVisible = Boolean(currentSound);

  // Same coordination for the consent dialog: while a first visit in an
  // opt-in regime still has the banner pending, it anchors to the same bottom
  // edge and covered this strip entirely (measured 237px on 390×844). Yield
  // to it, as for the mini-player, rather than paint underneath it; the strip
  // returns the moment the banner is answered (the hook listens for it).
  const { hasConsent } = useCookieConsent();

  // §5.3 — sticky mobile install CTA. Reveals once the visitor scrolls past
  // ~40% of the page; hides while a text field is focused so it never covers
  // the keyboard. Mobile-only — the strip itself is `sm:hidden`.
  useEffect(() => {
    let keyboardOpen = false;
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = scrollable > 0 ? window.scrollY / scrollable : 0;
      setShowStickyCta(!keyboardOpen && ratio > 0.4);
    };
    const onFocusIn = (e: FocusEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) {
        keyboardOpen = true;
        setShowStickyCta(false);
      }
    };
    const onFocusOut = () => {
      keyboardOpen = false;
      onScroll();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("focusin", onFocusIn);
    window.addEventListener("focusout", onFocusOut);
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("focusin", onFocusIn);
      window.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  const showInstallSteps = downloadCompleted && Boolean(onShowInstallSteps);
  const docked = showStickyCta && !miniPlayerVisible && hasConsent;

  // While docked, publish the strip's height on <html> so the document's
  // scroll-padding-bottom (app/globals.css) keeps focus and scrolled-to
  // content clear of it, as it does for the mini player and consent banner.
  const stripRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const strip = stripRef.current;
    if (!docked || !strip) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty(INSTALL_CTA_HEIGHT_VAR, `${Math.ceil(strip.getBoundingClientRect().height)}px`);
    publish();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    observer?.observe(strip);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(INSTALL_CTA_HEIGHT_VAR);
    };
  }, [docked]);

  return (
    <AnimatePresence>
      {docked && (
        <m.div
          ref={stripRef}
          key="sticky-install-cta"
          initial={rm ? { opacity: 0, y: 0 } : { y: 80 }}
          animate={rm ? { opacity: 1, y: 0 } : { y: 0 }}
          exit={rm ? { opacity: 0, y: 0 } : { y: 80 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          className="fixed inset-x-0 bottom-0 z-40 sm:hidden"
          data-yields-to-consent
          data-install-cta-docked
          style={{
            padding: "12px 16px calc(12px + env(safe-area-inset-bottom))",
            background: "var(--tls-canvas)",
            borderTop: "1px solid var(--tls-border-subtle)",
          }}
        >
          <button type="button"
            onClick={showInstallSteps ? onShowInstallSteps : onDownload}
            data-cuelume-press="press"
            data-cuelume-release={showInstallSteps ? "scan" : "pulse"}
            disabled={!showInstallSteps && (disabled || isDownloading)}
            aria-label={showInstallSteps ? "See install steps" : "Download LockChime.wav"}
            // §12.3: the scale press cue is motion-safe: gated, so a
            // reduced-motion user gets no confirmation their press
            // registered without a fallback — reuse this button's own
            // hover:opacity-90 target under motion-reduce:active.
            className="tlsr-focus flex w-full items-center justify-center gap-2 rounded-[var(--tls-radius-pill)] text-sm font-bold transition-[opacity,scale] duration-[var(--motion-duration-fast)] ease-[var(--motion-ease-standard)] hover:opacity-90 motion-safe:active:scale-[0.97] motion-reduce:active:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            // The one primary treatment per page is the accent fill
            // (PRODUCT.md "Decided: the primary button is the accent fill");
            // this strip IS the primary CTA once it is on screen, and the
            // ink fill it used to carry was a second primary treatment.
            style={{
              padding: "15px 22px",
              background: "var(--tls-brand)",
              color: "var(--tls-brand-fg)",
              border: "none",
            }}
          >
            {showInstallSteps ? (
              <>
                See install steps
              </>
            ) : isDownloading ? (
              <>
                <Spinner size="sm" />
                Downloading…
              </>
            ) : (
              <>
                <Download className="size-4" />
                Download LockChime.wav
              </>
            )}
          </button>
        </m.div>
      )}
    </AnimatePresence>
  );
}
